/**
 * SoundProvider — owns the single SoundEngine for the tab and exposes it
 * via `useSound()`. Also installs one-time gesture listeners so the
 * AudioContext is unlocked as soon as the user interacts (WebAudio autoplay
 * policy), and mounts the headless {@link SoundController}.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactElement,
  type ReactNode,
} from "react";
import { SoundEngine, type SoundEngineApi } from "./SoundEngine.js";
import { SoundController } from "./SoundController.js";

const SoundCtx = createContext<SoundEngineApi | null>(null);

export interface SoundProviderProps {
  readonly children: ReactNode;
  /** Inject a fake engine in tests; production builds a real one. */
  readonly engine?: SoundEngineApi;
}

export function SoundProvider({ children, engine }: SoundProviderProps): ReactElement {
  const eng = useMemo<SoundEngineApi>(() => engine ?? new SoundEngine(), [engine]);

  // Unlock audio on user gestures. NOT one-shot: a single missed/captured
  // first gesture (Pixi canvas, stopPropagation, etc.) or a context that
  // gets re-suspended (tab backgrounded) would otherwise leave audio dead.
  // unlock() is idempotent and cheap, so re-running it on every gesture is
  // safe and self-healing.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const unlock = (): void => {
      eng.unlock();
    };
    const opts = { passive: true, capture: true } as const;
    window.addEventListener("pointerdown", unlock, opts);
    window.addEventListener("keydown", unlock, opts);
    window.addEventListener("touchstart", unlock, opts);
    return () => {
      window.removeEventListener("pointerdown", unlock, opts);
      window.removeEventListener("keydown", unlock, opts);
      window.removeEventListener("touchstart", unlock, opts);
    };
  }, [eng]);

  // Dispose the engine only when we created it (an injected engine is the
  // caller's to manage).
  useEffect(() => {
    if (engine !== undefined) return;
    return () => {
      eng.dispose();
    };
  }, [eng, engine]);

  return (
    <SoundCtx.Provider value={eng}>
      <SoundController />
      {children}
    </SoundCtx.Provider>
  );
}

export function useSound(): SoundEngineApi {
  const v = useContext(SoundCtx);
  if (v === null) {
    throw new Error("useSound: must be used within <SoundProvider>");
  }
  return v;
}
